<!-- eslint-disable max-lines -->
<template>
  <div>
    <section class="form-section">
      <h1 class="form-section__title">
        <span>{{ $t('PUBLISHER_ACCOUNT.CEO_NATIONAL_INFO') }}</span>
        <a
          v-if="$auth.check(['publisher', 'changeFinanceInfo']) && !isVerified"
          v-show="!editable.nationalInfo"
          class="form-section__action-btn"
          role="button"
          @click.prevent="editable.nationalInfo = true"
        >
          <i class="el-icon-edit form-section__action-btn-icon" />
          <span class="form-section__edit-title">
            {{ $t('EDIT') }}
          </span>
        </a>
      </h1>

      <div class="row align-justify">
        <div class="small-12 large-4 columns">
          <p class="form-section__description">
            <span v-if="!isVerified">
              {{ $t('PUBLISHER_ACCOUNT.CEO_NATIONAL_INFO_DESC') }}
            </span>
            <RouterLinkFormat
              :links="{
                support: { to: { path: '/support' }, text: $t('SUPPORT') },
              }"
            >
              {{ $t('EDIT_DISABLED_DESC') }}
            </RouterLinkFormat>
          </p>
        </div>

        <div class="small-12 large-7 columns">
          <ElForm
            ref="nationalInfo"
            class="card"
            :rules="rules"
            :model="legalInfoModel"
            label-position="top"
            @submit.native.prevent="updateIdentificationInfo"
          >
            <div
              v-if="
                $auth.check(['publisher', 'changeFinanceInfo']) && !isVerified
              "
              v-show="editable.nationalInfo"
            >
              <div class="card__block">
                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.CEO_NATIONAL_CARD')"
                  required
                >
                  <Droppable
                    v-if="hasProps(legalInfoModel)"
                    :has-preview="true"
                    :has-progress="true"
                    file-types="image/*"
                    :upload-url="uploadPaths.nationalCard"
                    :image-url="legalInfoModel.ceo_national_card.url"
                    @uploaded="updateCeoNationalImage"
                  >
                    <div slot="label">
                      {{ $t('PUBLISHER_ACCOUNT.DRAG_CEO_NATIONAL_IMAGE') }}
                    </div>
                    <div slot="loading">
                      Loading...
                    </div>
                  </Droppable>
                </ElFormItem>

                <ElButton type="primary" native-type="submit" size="large">
                  {{ $t('SAVE') }}
                </ElButton>

                <ElButton
                  type="text"
                  @click.native="editable.nationalInfo = false"
                >
                  {{ $t('CANCEL') }}
                </ElButton>
              </div>
            </div>

            <div v-show="!editable.nationalInfo">
              <div class="card__block">
                <dl class="list list--definition list--clean">
                  <dt>{{ $t('PUBLISHER_ACCOUNT.CEO_NATIONAL_CARD') }}</dt>
                  <dd>
                    <img
                      v-if="hasProps(legalInfo)"
                      class="droppable__image"
                      :src="legalInfo.ceo_national_card.url"
                    />
                  </dd>
                </dl>
              </div>
            </div>
          </ElForm>
        </div>
      </div>
    </section>

    <hr class="divider" />

    <section class="form-section">
      <h1 class="form-section__title">
        {{ $t('PUBLISHER_ACCOUNT.COMPANY_INFO') }}
        <a
          v-if="$auth.check(['publisher', 'changeFinanceInfo']) && !isVerified"
          v-show="!editable.companyInfo"
          class="form-section__action-btn"
          role="button"
          @click.prevent="editable.companyInfo = true"
        >
          <i class="el-icon-edit form-section__action-btn-icon" />
          <span class="form-section__edit-title">
            {{ $t('EDIT') }}
          </span>
        </a>
      </h1>

      <div class="row align-justify">
        <div class="small-12 large-4 columns">
          <p class="form-section__description">
            <span v-if="!isVerified">
              {{ $t('PUBLISHER_ACCOUNT.COMPANY_INFO_DESC') }}
            </span>
            <RouterLinkFormat
              :links="{
                support: { to: { path: '/support' }, text: $t('SUPPORT') },
              }"
            >
              {{ $t('EDIT_DISABLED_DESC') }}
            </RouterLinkFormat>
          </p>
        </div>

        <div class="small-12 large-7 columns">
          <ElForm
            ref="companyInfo"
            class="card"
            :rules="rules"
            :model="legalInfoModel"
            label-position="top"
            @submit.native.prevent="updateCompanyInfo"
          >
            <div
              v-if="
                $auth.check(['publisher', 'changeFinanceInfo']) && !isVerified
              "
              v-show="editable.companyInfo"
            >
              <div class="card__block">
                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.COMPANY_CERTIFICATE')"
                  required
                >
                  <Droppable
                    v-if="hasProps(legalInfoModel)"
                    :has-preview="true"
                    :has-progress="true"
                    file-types="image/*"
                    :upload-url="uploadPaths.companyCertificate"
                    :image-url="legalInfoModel.company_certificate.url"
                    @uploaded="updateCertImage"
                  >
                    <div slot="label">
                      {{ $t('PUBLISHER_ACCOUNT.DRAG_CERTIFICATION_IMAGE') }}
                    </div>
                  </Droppable>
                </ElFormItem>

                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.COMPANY_NAME')"
                  prop="name"
                >
                  <ElInput v-model="legalInfoModel.name" name="name" />
                </ElFormItem>

                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER')"
                  prop="registration_number"
                >
                  <ElInput
                    v-model="legalInfoModel.registration_number"
                    name="registration_number"
                  />
                </ElFormItem>

                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.COMPANY_NATIONAL_ID')"
                  prop="national_id"
                >
                  <ElInput
                    v-model="legalInfoModel.national_id"
                    name="national_id"
                  />
                </ElFormItem>

                <ElButton type="primary" native-type="submit" size="large">
                  {{ $t('SAVE') }}
                </ElButton>

                <ElButton
                  type="text"
                  @click.native="editable.companyInfo = false"
                >
                  {{ $t('CANCEL') }}
                </ElButton>
              </div>
            </div>

            <div v-show="!editable.companyInfo">
              <div class="card__block">
                <dl class="list list--definition list--clean">
                  <dt>{{ $t('PUBLISHER_ACCOUNT.COMPANY_CERTIFICATE') }}</dt>
                  <dd>
                    <img
                      v-if="hasProps(legalInfo)"
                      class="droppable__image"
                      :src="legalInfo.company_certificate.url"
                    />
                  </dd>

                  <dt>{{ $t('PUBLISHER_ACCOUNT.COMPANY_NAME') }}</dt>
                  <dd>{{ legalInfo.name }}</dd>

                  <dt>{{ $t('PUBLISHER_ACCOUNT.REGISTRATION_NUMBER') }}</dt>
                  <dd>{{ legalInfo.registration_number }}</dd>

                  <dt>{{ $t('PUBLISHER_ACCOUNT.COMPANY_NATIONAL_ID') }}</dt>
                  <dd>{{ legalInfo.national_id }}</dd>
                </dl>
              </div>
            </div>
          </ElForm>
        </div>
      </div>
    </section>

    <hr class="divider" />

    <section class="form-section">
      <h1 class="form-section__title">
        {{ $t('PUBLISHER_ACCOUNT.FINANCE_INFO') }}
        <a
          v-if="$auth.check(['publisher', 'changeFinanceInfo']) && !isVerified"
          v-show="!editable.financeInfo"
          class="form-section__action-btn"
          role="button"
          @click.prevent="editable.financeInfo = true"
        >
          <i class="el-icon-edit form-section__action-btn-icon" />
          <span class="form-section__edit-title">
            {{ $t('EDIT') }}
          </span>
        </a>
      </h1>

      <div class="row align-justify">
        <div class="small-12 large-4 columns">
          <p
            v-if="$t('PUBLISHER_ACCOUNT.FINANCE_INFO_DESC')"
            class="form-section__description"
          >
            <span v-if="!isVerified">
              {{ $t('PUBLISHER_ACCOUNT.FINANCE_INFO_DESC') }}
            </span>
            <RouterLinkFormat
              :links="{
                support: { to: { path: '/support' }, text: $t('SUPPORT') },
              }"
            >
              {{ $t('EDIT_DISABLED_DESC') }}
            </RouterLinkFormat>
          </p>
        </div>

        <div class="small-12 large-7 columns">
          <ElForm
            ref="financeInfo"
            class="card"
            :rules="rules"
            :model="legalInfoModel"
            label-position="top"
            @submit.native.prevent="updateFinanceInfo"
          >
            <div
              v-if="
                $auth.check(['publisher', 'changeFinanceInfo']) && !isVerified
              "
              v-show="editable.financeInfo"
            >
              <div class="card__block">
                <ElFormItem :label="$t('PUBLISHER_ACCOUNT.VAT_IMAGE')" required>
                  <Droppable
                    :has-preview="true"
                    :has-progress="true"
                    file-types="image/*"
                    :upload-url="uploadPaths.vat"
                    :image-url="legalInfoModel.vat_image.url"
                    @uploaded="updateVatImage"
                  >
                    <div slot="label">
                      {{ $t('PUBLISHER_ACCOUNT.DRAG_VAT_IMAGE') }}
                    </div>
                  </Droppable>
                </ElFormItem>

                <ElFormItem
                  :label="$t('PUBLISHER_ACCOUNT.ECONOMIC_CODE')"
                  prop="economic_code"
                >
                  <ElInput
                    v-model="legalInfoModel.economic_code"
                    name="economic_code"
                  />
                </ElFormItem>

                <ElButton type="primary" native-type="submit" size="large">
                  {{ $t('SAVE') }}
                </ElButton>

                <ElButton
                  type="text"
                  @click.native="editable.financeInfo = false"
                >
                  {{ $t('CANCEL') }}
                </ElButton>
              </div>
            </div>

            <div v-show="!editable.financeInfo">
              <div class="card__block">
                <dl class="list list--definition list--clean">
                  <dt>{{ $t('PUBLISHER_ACCOUNT.VAT_IMAGE') }}</dt>
                  <dd>
                    <img
                      v-if="hasProps(legalInfo)"
                      class="droppable__image"
                      :src="legalInfo.vat_image.url"
                    />
                  </dd>

                  <dt>{{ $t('PUBLISHER_ACCOUNT.ECONOMIC_CODE') }}</dt>
                  <dd>{{ legalInfo.economic_code }}</dd>
                </dl>
              </div>
            </div>
          </ElForm>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Droppable from '@@/layout/Droppable';
import { Publishers } from '@/api/api';
import { nonEmptyProps, hasProps } from '@/utils/object';
import form from '@/mixins/form';
import normalizeNumbers from '@/utils/normalizeNumbers';
import RouterLinkFormat from '@@/common/RouterLinkFormat';

export default {
  components: {
    Droppable,
    RouterLinkFormat,
  },
  mixins: [form],

  props: {
    legalInfo: {
      type: Object,
      default() {
        return {};
      },
    },

    isVerified: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      legalInfoModel: Object.assign({}, this.legalInfo),
      uploadPaths: Publishers.uploadPaths,
      editable: {
        nationalInfo: false,
        financeInfo: false,
        companyInfo: false,
      },
    };
  },

  computed: {
    financeInfo() {
      const model = this.legalInfoModel;

      return nonEmptyProps({
        vat_image: model.vat_image.id,
        economic_code: model.economic_code,
      });
    },

    nationalInfo() {
      const model = this.legalInfoModel;

      return nonEmptyProps({
        name: model.name,
        ceo_national_card: model.ceo_national_card.id,
      });
    },

    companyInfo() {
      const model = this.legalInfoModel;

      return nonEmptyProps({
        name: model.name,
        registration_number: model.registration_number,
        company_certificate: model.company_certificate.id,
        national_id: model.national_id,
      });
    },

    rules() {
      return {
        name: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        registration_number: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        national_id: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        vat_image: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
        economic_code: {
          required: true,
          message: this.$t('INVALID_INPUT_VALUE'),
        },
      };
    },
  },

  watch: {
    legalInfo() {
      this.legalInfoModel = Object.assign({}, this.legalInfo);
    },
  },

  methods: {
    hasProps,

    updateCertImage(src) {
      this.legalInfoModel.company_certificate.id = src;
    },

    updateCeoNationalImage(src) {
      this.legalInfoModel.ceo_national_card.id = src;
    },

    updateVatImage(src) {
      this.legalInfoModel.vat_image.id = src;
    },

    updateLegal(type, modelName) {
      if (
        !this.$auth.check(['publisher', 'changeFinanceInfo']) ||
        !this.isValid(modelName)
      ) {
        return false;
      }

      return (
        Publishers.updateLegal({ type }, normalizeNumbers(this[modelName]))
          .then(
            ({
              body: { message, identification_info: identificationInfo },
            }) => {
              this.legalInfoModel = Object.assign(
                {},
                this.legalInfoModel,
                identificationInfo,
              );

              this.$emit('updatedLegalInfo', this.legalInfoModel);
              this.$message.success(message);
            },
          )
          // switches to the read-only mode
          .then(() => {
            this.editable[modelName] = false;
          })
          .catch(({ body: { message } = {} }) => {
            if (message) {
              this.$message.error(message);
            }
          })
      );
    },

    updateFinanceInfo() {
      return this.updateLegal('financial', 'financeInfo');
    },

    updateIdentificationInfo() {
      return this.updateLegal('identification', 'nationalInfo');
    },

    updateCompanyInfo() {
      return this.updateLegal('identification', 'companyInfo');
    },
  },
};
</script>
